<template>
  <div id="svg_container">
    <tooltip 
      v-on:close-tooltip="handleEventCloseTooltipClick" 
      :x="tooltip.x" 
      :y="tooltip.y" 
      :data="tooltip.data" 
      :sticky=false />
  </div>
</template>

<script>
import * as d3 from "d3"; // Do you really wanna import erything???
import * as topojson from "topojson";

import Slider from "@/components/Slider.vue";
import Tooltip from "@/components/Tooltip.vue";

export default {
  name: "Map",
  props: {
    dataset: null,
    datasource: null,
    title: {
      type: String,
      default: "Title?"
    },
    height: {
      type: Number,
      default: 600
    },
    width: {
      type: Number,
      default: 1080
    },
  },
  components: {
    Slider,
    Tooltip
  },
  data: function() {
    return {
      zoom: d3.zoom()
      .scaleExtent([.6, 10])
      .on("zoom", this.onZoomed ),
      topojson: null,
      topojsonSource: "https://d3js.org/us-10m.v2.json",
      svg: null,
      tooltipWidth: 147,
      eventClickData: null,
      selectedEvent: null,

      error: false,
      loadData: [],
      eventsCount: 0,
      events: [],
      dates: [],
      eventRadius: 4,
      baseLandStrokeWidth: 0.05,
      land:{
        strokeWidth: 0.05
      },
      tooltip: {
        x: -99999,
        y: -99999,
        data: {}
      },
      state: "all",
      ransomewareType: "all",
      randsomewareAmountRange: "all",
      filterOptions: {
        // TODO: Derive these values from events data.
        states: ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania", "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"],
        ransomewareTypes: ["Bitpaymer", "Cerber", "CryptoLocker", "CryptoWall", "CrytoWall", "Dharma", "GandCrab", "Global 3", "HDDCryptor", "Hermes", "LeChiffre", "LockCrypt", "Megacode", "Nemesis", "OSIRIS", "PewCrypt", "RobbinHood", "Ryuk", "SamSam", "Scarab", "TeslaCrypt", "WannaCry", "UNKNOWN"],
        randsomewareAmounts:{
          "$0 - $999":[0, 999],
          "$1,000 - $9,999":[1000, 9999],
          "$10,000 - $99,999":[10000,99999],
          "$100,000 - $999,999":[100000,999999],
          "$1 million+":[1000000, null],
          "UNKOWN":[null,null]
        }
      },
    };
  },
  watch: {
    selectedEvent: function(circle, prevData){
      // remove currently active events and add active class to 'circle'
      d3.selectAll('circle.event.active').attr('class','event')
      d3.select(circle).attr('class','event active')
    },
    topojson: function(data, prevData){
      if(!data) return;

      // Draw geography
      let path  = d3.geoPath();
      let { states, nation } = data.objects;
      // Draw nation
      let svg = this.svg.select(".topojson")

      svg.append("path")
      .attr("class", "land")
      .style('stroke-width', `${this.land.strokeWidth}em`)
      .datum(topojson.feature(data, nation))
      .attr("d", path);
      
      // Draw state borders
      svg.append("path")
      .attr("class", "border border--state")
      .style('stroke-width', `${this.land.strokeWidth}em`)
      .datum(
        topojson.mesh(data, states, function(a, b) {
          return a !== b;
        })
      )
      .attr("d", path);
    },
    dataset: function(data, prevData){
      if(!data) return;
      let svg = this.svg;
      let projection = d3
        .geoAlbersUsa()
        .scale(1280)
        .translate([480, 300]);
      
      this.svg.selectAll(".event").remove()

      this.svg.select(".dataset")
      .selectAll("text")
      .data(data)
      .enter()
      .append("circle")
      .attr("class", "event")
      .attr("r", () => this.eventRadius )
      .attr("transform", function(event) {
        let coordinates = projection([event.Lon, event.Lat]);
        if(!coordinates){
          coordinates = '-99999,-99999'
          console.warn(`Lat/Long [${event.Lat}, ${event.Lon}] could not be transformed by D3js`)
        }
        return "translate(" + coordinates + ")"
      })
      .on("mousemove", this.handleEventMove)
      .on("mouseout", this.handleEventOut)
      .on("click", this.handleEventClick)
      .attr("text-anchor", "middle")
    },
    land: function(data, prevData){
      if(!data) return;
      d3.select('path.land').style('stroke-width', `${this.land.strokeWidth}em`)
      d3.select('path.border--state').style('stroke-width', `${this.land.strokeWidth}em`)
    },
  },
  created() {
    window.d3 = d3;
    this.loadTopoJson().then( topojson => {
      this.$emit('topojson-loaded', topojson)
      this.topojson = topojson
      // window.topojson = topojson
    })
  },
  mounted() {
    // changes to data will reload the component.
    // add zoom functionalities
    // see https://www.d3-graph-gallery.com/graph/interactivity_zoom.html#axisZoom
    let svg = this.svg = d3.select("#svg_container")
    .append("svg")
    .attr("version","1.1")
    .attr("viewBox","-30 -20 1000 1000")
    // .call(d3.zoom().on("zoom", function () {
    //   svg.attr("transform", d3.event.transform)
    // }))
    .call(this.zoom)
    .append("g")
    .attr("class", "svg-content")


    // geographic layer
    svg.append("g")
    .attr("class", "topojson")
    // data layer
    svg.append("g")
    .attr("class", "dataset")   
    
    // TODO synch theses state with onZoom in this.Zoom.
    // See https://observablehq.com/@d3/programmatic-zoom
    // window.onZoomIn   = this.onZoomIn
    // window.onZoomOut  = this.onZoomOut
    // window.onReset    = this.onReset
  },
  updated() {

    if(this.tooltip.data){
      let height        = document.body.offsetHeight || document.documentElement.offsetHeight
      let width         = document.body.offsetWidth || document.documentElement.offsetWidth
      let tooltipWidth  = document.getElementById("tooltip").offsetWidth
      let tooltipHeight = document.getElementById("tooltip").offsetHeight
      let markerX       = this.eventClickData.event.offsetX
      let markerY       = this.eventClickData.event.offsetY

      if( (width - markerX) < tooltipWidth ){
        this.tooltip.x  = markerX - tooltipWidth
      }
      let snap_bottom_treshold = width < 766 ? 620 : 500
      // console.log(`h(${height}) - t.y(${this.tooltip.y}) = ${height - this.tooltip.y} <= ${snap_bottom_treshold} `)
      if( (height - this.tooltip.y) <= snap_bottom_treshold ){
        // console.log('snap bottom')
        this.tooltip.y -= 10
      }
    }

  },
  methods: {
    isInRange(value, min, max, unknow='UNKNOWN'){
      let result  = false
      if( (value >= min) && (value <= max)){
        result    = true
      } else if( (max == null) && (min == null) ){
        result  = value == unknow
      }
      return result
    },
    parseCurrency(currency=""){
      return currency.replace(/\$|,/gi,'')
    },
    async loadTopoJson() {
      return await d3.json(this.topojsonSource)
      .catch( error => {
        this.$emit('topojson-error', error)
      })
    },
    handleSvgClick(event){
      // console.log('SVG clicked. closing tooltip!')
      let isCircle = event.target.tagName == "circle"
      if(this.tooltip.data && !isCircle){
        this.handleEventCloseTooltipClick()
      }
    },
    handleEventCloseTooltipClick(){
      d3.select(this.selectedEvent).attr('class','event')
      if(!this.tooltip.sticky){
        this.tooltip.data = null;
      }
    },
    handleEventClick(event, i, g) {
      // (d, i, g) => console.log(d, i, g)
      // window.event = {e:event,i:i,g:g}
      this.selectedEvent = g[i]
      this.eventClickData = {
        event:d3.event, 
        data: event,
      }
      
      let { 
        // x, unreliable
        // y, unreliable
        offsetX,  // tooltip
        offsetY,  // tooltip
        clientX,  // circle
        clientY,  // circle
      } = d3.event;

      this.tooltip.x = offsetX;
      this.tooltip.y = offsetY;
      this.tooltip.data = event;
    },
    handleEventMove(event) {
      // let { x, y } = d3.event;
      // this.tooltip.x = x;
      // this.tooltip.y = y;
      // this.tooltip.data = event;
    },
    handleEventOut() {
    },
    onZoomed() {
      let { k }     = d3.event.transform
      let newRadius = this.eventRadius - k*0.36
      newRadius     = newRadius > 1 ? newRadius : newRadius

      console.log('newRadius:',newRadius, 'k:', k)
      // update event circle radius
      this.svg
      .selectAll("circle.event")
      .attr("r", newRadius)
      
      if(k <= 4.3){
        let newStrokeWidth = 0.05-(k/100)
        this.land = {
          ...this.land,
          strokeWidth: newStrokeWidth
        }
      }
      // set transform
      this.svg.attr("transform", d3.event.transform )
    },
    onZoomIn() {
      this.svg.transition().call( this.zoom.scaleBy, 1.5 )
    },
    onZoomOut() {
      this.svg.transition().call( this.zoom.scaleBy, 0.5 )
    },
    onReset() {
      this.svg.call(
        this.zoom.transform,
        d3.zoomIdentity
      )
    }
  }
};


</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";

#svg_container {
  // height: 0;
  width: 100%;
  // padding-top: 48%;
  position: relative;
}

// responsive svg
// see http://thenewcode.com/744/Make-SVG-Responsive
.svg-container { 
	display: inline-block;
	position: relative;
	width: 100%;
	padding-bottom: 100%; 
	vertical-align: middle; 
	overflow: hidden; 
  background-color: #525252;
}
.svg-content { 
	display: inline-block;
	position: absolute;
	top: 0;
  left: 0;
}

.map.wrapper {
  background-color: #6370c2;
  margin: 0px !important;
}

.top-wrap{
  position: absolute;
  margin: 0 auto;
  max-width: 1080px;
  width: 100%;
}
.mapname{
  float: left;
  font-family: Anton, sans-serif;
  font-size: 40px;
  color: #fff;
  line-height: 38px;
  padding-left: 10px;
}
.filter{
  float: right;
  font-family: 'Roboto Mono',sans-serif;
  font-weight: bold;
  font-size: 10px;
  color: #fff;
  text-align: left;
  background-color: #000;
}
.filter table tr td{
  width: 120px;
  padding-right: 20px;
}
.filter table tr td:first-child{
  width: 120px;
  padding-right: 0px;
}
.zoom div.icon {
  cursor: pointer;
}
/* class applies to select element itself, not a wrapper element */
select{
  text-align: right;
  align-items: right;
}
.select-css {
	display: block;
	font-size: 10px;
  font-family: "Roboto Mono",sans-serif;
  text-align: right;
	font-weight: bold;
	color: #fff;
	line-height: 1.3;
	padding: .6em 1.4em .5em .8em;
	width: 100%;
	max-width: 100%; /* useful when width is set to anything other than 100% */
	box-sizing: border-box;
  margin: 0;
  border: none;
	border-bottom: 1px solid #fff;
	/*box-shadow: 0 1px 0 1px rgba(0,0,0,.04);*/
	border-radius: 0px;
	-moz-appearance: none;
	-webkit-appearance: none;
	appearance: none;
	background-color: #000;
	/* note: bg image below uses 2 urls. The first is an svg data uri for the arrow icon, and the second is the gradient. 
		for the icon, if you want to change the color, be sure to use `%23` instead of `#`, since it's a url. You can also swap in a different svg icon or an external image reference
		
	*/
	background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23ffffff%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E'),
	  linear-gradient(to bottom, #000 0%,#000 100%);
	background-repeat: no-repeat, repeat;
	/* arrow icon position (1em from the right, 50% vertical) , then gradient position*/
	background-position: right .7em top 50%, 0 0;
	/* icon size, then gradient */
	background-size: .65em auto, 100%;
}
/* Hide arrow icon in IE browsers */
.select-css::-ms-expand {
	display: none;
}
/* Hover style */
.select-css:hover {
  border-color: #000;
  border-bottom: 1px solid #fff;
}
/* Focus style */
.select-css:focus {
	border-color: #000;
	/* It'd be nice to use -webkit-focus-ring-color here but it doesn't work on box-shadow */
	/*box-shadow: 0 0 1px 3px rgba(59, 153, 252, .7);
	box-shadow: 0 0 0 3px -moz-mac-focusring;*/
	color: #fff; 
  outline: none;
  border-bottom: 1px solid #fff;
}

/* Set options to normal weight */
.select-css option {
  font-weight:normal;
  text-align: right;
  border-bottom: 1px solid #fff;
}

/* Support for rtl text, explicit support for Arabic and Hebrew */
*[dir="rtl"] .select-css, :root:lang(ar) .select-css, :root:lang(iw) .select-css {
	background-position: left .7em top 50%, 0 0;
	padding: .6em .8em .5em 1.4em;
}

/* Disabled styles */
.select-css:disabled, .select-css[aria-disabled=true] {
	color: graytext;
	/*background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22graytext%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E'),
	  linear-gradient(to bottom, #ffffff 0%,#e5e5e5 100%);*/
}

.select-css:disabled:hover, .select-css[aria-disabled=true] {
	border-color: #fff;
}
/*end select styles */
.land {
  fill: $land-color;
  stroke: $border-color;
}
.border {
  fill: none;
  stroke: $border-color;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.event {
  fill: $event-fill-color;
}
.event.active,
.event:hover {
  cursor: pointer;
  fill: #6370c2;
}
.wrapper--map {
  display: flex;
  justify-content: center;
}
.zoom{
  // display: none;
  float: right;
  margin-top: -30px;
}
.zoom .icon{
  display: block;
  height: 30px;
  width: 30px;
  background-color: #ccc;
  color: #000;
  border-top: 1px solid #000;
}

.fa{
  margin-top: 6px;
}

.filter table tr td:first-child{
  width: 95%;
  padding-right: 0px;
  padding-top: 10px;
  font-size: 15px;
}
.select-css{
  font-size: 12px;
}
.stats-wrap{
  padding-left: 10px;
}


@media screen and (max-width: 759px){
  .filter{
    padding-left: 10px;
    padding-right: 10px;
    float: left;
  }
  .filter table tr td{
  width: 95%;
  padding-right: 0px;
  padding-top: 10px;
  font-size: 15px;
}

}
</style>